import axios, { AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  LoginRequest, 
  RegisterRequest, 
  ChangePasswordRequest, 
  CreateHelperRequest, 
  HelperSearchRequest, 
  HelperProfile, 
  User, 
  JobPosting, 
  CreateJobPostingRequest, 
  JobPostingSearchRequest, 
  JobApplication, 
  CreateJobApplicationRequest, 
  VipPlan, 
  PaymentResponse, 
  PaymentRecord, 
  CreatePaymentRequest,
  DataDictionary,
  ContentNodeResponse
} from '../types';
import envConfig from '../config/env';

// API 基礎 URL 配置
const API_BASE_URL = envConfig.apiUrl;

const getStoredToken = (): string | null =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

export const clearStoredAuth = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};


const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch { return null; }
};

export const isTokenExpired = (token: string | null | undefined): boolean => {
  if (!token || token.trim().length === 0) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }

  return Date.now() >= payload.exp * 1000;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Client': 'helper-web',
  },
  withCredentials: false,
});

// 請求攔截器
api.interceptors.request.use(
  (config) => {
    // 在開發模式下打印請求信息
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
      });
    }
    
    // 從 sessionStorage 和 localStorage 獲取 token（支援跨標籤頁）
    const token = getStoredToken();
    if (token) {
      if (isTokenExpired(token)) {
        clearStoredAuth();
        // auth cleared; ProtectedRoute handles redirect
        return Promise.reject(new Error('JWT expired before request dispatch'));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 從 localStorage 和 sessionStorage 獲取用戶信息（支援跨標籤頁）
    const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        if (user && user.username) {
          config.headers['X-Username'] = user.username;
        }
      } catch (e) {
        // 忽略解析錯誤
      }
    }
    
    // 添加 CORS 相關頭部（如果需要）
    config.headers['Accept'] = 'application/json';
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// 響應攔截器
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // 在開發模式下打印響應信息
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }
    return response.data;
  },
  async (error) => {
    // 詳細的錯誤日誌
    console.error('API Error:', {
      message: error.message,
      code: error.code,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      } : null,
      request: error.request ? {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
      } : null,
    });
    
    // 處理 CORS 錯誤
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      console.error('CORS 或網絡錯誤：', {
        message: '無法連接到後端服務器',
        apiUrl: API_BASE_URL,
        suggestion: '請檢查：1. 後端服務是否運行 2. CORS 配置是否正確 3. API URL 是否正確',
      });
    }
    
    if (error.response?.status === 401) {
      clearStoredAuth();
      // ProtectedRoute handles redirect
    }

    // 403 = 權限不足（Forbidden），不代表 token 無效。
    // 不清除認證也不重定向，讓呼叫端自行處理（例如顯示錯誤訊息）。
    // 只有 401 才表示 token 過期或無效，需要重新登入。
    if (error.response?.status === 403) {
      const token = getStoredToken();
      if (token && isTokenExpired(token)) {
        clearStoredAuth();
        // auth cleared; ProtectedRoute handles redirect
      }
    }
    
    return Promise.reject(error);
  }
);

export const apiService = {
  // 認證相關
  auth: {
    login: async (credentials: LoginRequest): Promise<ApiResponse<{ token: string; user: User }>> => {
      return api.post('/auth/login', credentials);
    },
    register: async (userData: RegisterRequest): Promise<ApiResponse<User>> => {
      return api.post('/auth/register', userData);
    },
  },

  // 用戶管理
  users: {
    getProfile: async (): Promise<ApiResponse<User>> => {
      return api.get('/auth/me');
    },
    updateProfile: async (userData: Partial<User>): Promise<ApiResponse<User>> => {
      return api.put('/auth/profile', userData);
    },
    changePassword: async (passwordData: ChangePasswordRequest): Promise<ApiResponse<void>> => {
      return api.post('/auth/change-password', passwordData);
    },
    forgotPasswordRequestCode: async (whatsappPhone: string, captchaToken?: string): Promise<ApiResponse<void>> => {
      return api.post('/auth/forgot-password/request-code', { whatsappPhone, captchaToken });
    },
    requestEmailVerification: async (email?: string): Promise<ApiResponse<void>> => {
      return api.post('/auth/email/request-verification', email ? { email } : {});
    },
    verifyEmail: async (token: string): Promise<ApiResponse<void>> => {
      return api.get('/auth/email/verify', { params: { token } });
    },
    forgotPasswordReset: async (whatsappPhone: string, code: string, newPassword: string): Promise<ApiResponse<void>> => {
      return api.post('/auth/forgot-password/reset', { whatsappPhone, code, newPassword });
    },
    requestWhatsappCode: async (newPhone: string): Promise<ApiResponse<void>> => {
      return api.post('/auth/whatsapp/request-code', { newPhone });
    },
    verifyWhatsappCode: async (code: string): Promise<ApiResponse<User>> => {
      return api.post('/auth/whatsapp/verify-code', { code });
    },
  },

  // 外佣管理
  helpers: {
    search: async (searchRequest: HelperSearchRequest): Promise<ApiResponse<{ content: HelperProfile[]; totalElements: number; totalPages: number; size: number; number: number }>> => {
      return api.post('/helpers/search', searchRequest);
    },
    getById: async (id: number): Promise<ApiResponse<HelperProfile>> => {
      return api.get(`/helpers/${id}`);
    },
    getMyProfile: async (): Promise<ApiResponse<HelperProfile>> => {
      return api.get('/helpers/me');
    },
    getByUserId: async (userId: number): Promise<ApiResponse<HelperProfile>> => {
      return api.get(`/helpers/user/${userId}`);
    },
    create: async (helperData: CreateHelperRequest): Promise<ApiResponse<HelperProfile>> => {
      return api.post('/helpers', helperData);
    },
    update: async (id: number, helperData: Partial<CreateHelperRequest>): Promise<ApiResponse<HelperProfile>> => {
      return api.put(`/helpers/${id}`, helperData);
    },
    uploadAvatar: async (id: number, file: File): Promise<ApiResponse<{ avatarUrl: string }>> => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`/helpers/${id}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    updateWorkExperiences: async (id: number, experiences: object[]): Promise<ApiResponse<any[]>> => {
      return api.put(`/helpers/${id}/work-experiences`, experiences);
    },
    uploadDocument: async (id: number, formData: FormData): Promise<ApiResponse<any>> => {
      return api.post(`/helpers/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    deleteDocument: async (helperId: number, documentId: number): Promise<ApiResponse<void>> => {
      return api.delete(`/helpers/${helperId}/documents/${documentId}`);
    },
    listDocuments: async (helperId: number, documentType?: string): Promise<ApiResponse<any[]>> => {
      return api.get(`/helpers/${helperId}/documents`, { params: documentType ? { documentType } : undefined });
    },
    getRecommendedJobs: async (helperProfileId: number, limit: number = 50): Promise<ApiResponse<any[]>> => {
      return api.get(`/helpers/${helperProfileId}/recommended-jobs`, { params: { limit } });
    },
    mismatchCheck: async (helperId: number, employerId?: number): Promise<ApiResponse<any>> => {
      return api.get(`/helpers/${helperId}/mismatch-check`, { params: employerId ? { employerId } : undefined });
    },
  },

  // 招聘貼文管理
  jobPostings: {
    create: async (jobPostingData: CreateJobPostingRequest): Promise<ApiResponse<JobPosting>> => {
      return api.post('/job-postings', jobPostingData);
    },
    getById: async (id: number): Promise<ApiResponse<JobPosting>> => {
      return api.get(`/job-postings/${id}`);
    },
    getByEmployer: async (employerId: number, page: number = 0, size: number = 20): Promise<ApiResponse<{ content: JobPosting[]; totalElements: number; totalPages: number; size: number; number: number }>> => {
      return api.get(`/job-postings/employer/${employerId}`, {
        params: { page, size }
      });
    },
    getActive: async (page: number = 0, size: number = 20): Promise<ApiResponse<{ content: JobPosting[]; totalElements: number; totalPages: number; size: number; number: number }>> => {
      return api.get('/job-postings/active', {
        params: { page, size }
      });
    },
    search: async (searchRequest: JobPostingSearchRequest): Promise<ApiResponse<{ content: JobPosting[]; totalElements: number; totalPages: number; size: number; number: number }>> => {
      return api.post('/job-postings/search', searchRequest);
    },
    update: async (id: number, jobPostingData: Partial<CreateJobPostingRequest>): Promise<ApiResponse<JobPosting>> => {
      return api.put(`/job-postings/${id}`, jobPostingData);
    },
    updateStatus: async (id: number, status: string, closeReason?: string): Promise<ApiResponse<JobPosting>> => {
      return api.put(`/job-postings/${id}/status`, null, {
        params: closeReason ? { status, closeReason } : { status }
      });
    },
    delete: async (id: number): Promise<ApiResponse<void>> => {
      return api.delete(`/job-postings/${id}`);
    },
    getRecommendedHelpers: async (id: number, limit: number = 10): Promise<ApiResponse<any[]>> => {
      return api.get(`/job-postings/${id}/recommended-helpers`, { params: { limit } });
    },
    getLegalMinimumWage: async (): Promise<ApiResponse<{ amount: number; currency: string; effectiveDate: string } | null>> => {
      return api.get('/job-postings/legal-minimum-wage');
    },
    getPendingHireChecks: async (): Promise<ApiResponse<JobPosting[]>> => {
      return api.get('/job-postings/hire-check/pending');
    },
    answerHireCheck: async (id: number, stage: '100' | '180', foundHelper: boolean): Promise<ApiResponse<JobPosting>> => {
      return api.post(`/job-postings/${id}/hire-check/answer`, null, {
        params: { stage, foundHelper }
      });
    },
  },

  // 招聘申請管理
  jobApplications: {
    apply: async (jobPostingId: number, request: CreateJobApplicationRequest): Promise<ApiResponse<JobApplication>> => {
      return api.post(`/job-applications/job-posting/${jobPostingId}`, request);
    },
    getByHelper: async (helperId: number): Promise<ApiResponse<JobApplication[]>> => {
      return api.get(`/job-applications/helper/${helperId}`);
    },
    getByJobPosting: async (jobPostingId: number): Promise<ApiResponse<JobApplication[]>> => {
      return api.get(`/job-applications/job-posting/${jobPostingId}`);
    },
    getByEmployer: async (employerId: number): Promise<ApiResponse<JobApplication[]>> => {
      return api.get(`/job-applications/employer/${employerId}`);
    },
    updateStatus: async (applicationId: number, status: string): Promise<ApiResponse<JobApplication>> => {
      return api.put(`/job-applications/${applicationId}/status`, null, {
        params: { status }
      });
    },
    withdraw: async (applicationId: number, helperId: number): Promise<ApiResponse<void>> => {
      return api.delete(`/job-applications/${applicationId}`, {
        params: { helperId }
      });
    },
  },

  helperTips: {
    getActive: async () => api.get('/helper-tips'),
  },

  // VIP 和支付相關
  vip: {
    getActivePlans: async (): Promise<ApiResponse<VipPlan[]>> => {
      return api.get('/vip/plans/active');
    },
    getPlanById: async (id: number): Promise<ApiResponse<VipPlan>> => {
      return api.get(`/vip/plans/${id}`);
    },
  },

  payments: {
    create: async (userId: number, request: CreatePaymentRequest): Promise<ApiResponse<PaymentResponse>> => {
      return api.post('/payments/create', request, {
        params: { userId }
      });
    },
    getUserPayments: async (userId: number): Promise<ApiResponse<PaymentRecord[]>> => {
      return api.get(`/payments/user/${userId}`);
    },
    getStatus: async (paymentIntentId: string, sign?: string): Promise<ApiResponse<PaymentResponse>> => {
      const params = sign ? `?sign=${encodeURIComponent(sign)}` : '';
      return api.get(`/payments/status/${paymentIntentId}${params}`);
    },
  },

  promo: {
    validate: async (code: string, planId?: number): Promise<ApiResponse<any>> => {
      return api.post('/promo/validate', { code, planId });
    },
  },

  // 候選人收藏管理
  candidateBookmarks: {
    add: async (helperId: number): Promise<ApiResponse<any>> => {
      return api.post(`/candidate-bookmarks/helper/${helperId}`);
    },
    remove: async (helperId: number): Promise<ApiResponse<void>> => {
      return api.delete(`/candidate-bookmarks/helper/${helperId}`);
    },
    getMyBookmarks: async (): Promise<ApiResponse<HelperProfile[]>> => {
      return api.get('/candidate-bookmarks/my-candidates');
    },
    check: async (helperId: number): Promise<ApiResponse<boolean>> => {
      return api.get(`/candidate-bookmarks/helper/${helperId}/check`);
    },
  },

  // 統計功能
  statistics: {
    recordJobPostBookmark: async (helperId: number, jobPostingId: number): Promise<ApiResponse<void>> => {
      return api.post('/statistics/job-post-bookmark', null, {
        params: { helperId, jobPostingId }
      });
    },
    recordHelperProfileView: async (helperId: number, viewerId?: number, viewerType?: string): Promise<ApiResponse<void>> => {
      return api.post('/statistics/helper-profile-view', null, {
        params: { helperId, viewerId, viewerType }
      });
    },
    recordJobPostingView: async (jobPostingId: number, viewerId?: number, viewerType?: string): Promise<ApiResponse<void>> => {
      return api.post('/statistics/job-posting-view', null, {
        params: { jobPostingId, viewerId, viewerType }
      });
    },
  },


  // Helper Job Bookmarks (with backend persistence)
  jobBookmarks: {
    add: async (jobPostingId: number): Promise<ApiResponse<number>> => {
      return api.post(`/job-bookmarks/job/${jobPostingId}`);
    },
    remove: async (jobPostingId: number): Promise<ApiResponse<void>> => {
      return api.delete(`/job-bookmarks/job/${jobPostingId}`);
    },
    getMyBookmarks: async (): Promise<ApiResponse<JobPosting[]>> => {
      return api.get('/job-bookmarks/my-jobs');
    },
    check: async (jobPostingId: number): Promise<ApiResponse<boolean>> => {
      return api.get(`/job-bookmarks/job/${jobPostingId}/check`);
    },
  },
  // 面試意向
  interviewIntents: {
    send: (employerId: number, helperProfileId: number) =>
      api.post(`/intents/employer/${employerId}/helper/${helperProfileId}`),
    accept: (token: string) =>
      api.get(`/intents/accept/${token}`),
    reject: (token: string) =>
      api.post(`/intents/reject/${token}`),
    check: (employerId: number, helperProfileId: number) =>
      api.get(`/intents/employer/${employerId}/helper/${helperProfileId}/check`),
    getByEmployer: (employerId: number) =>
      api.get(`/intents/employer/${employerId}`),
    getByHelper: (helperProfileId: number) =>
      api.get(`/intents/helper/${helperProfileId}`),
    getRemainingQuota: (employerId: number): Promise<ApiResponse<number>> =>
      api.get(`/intents/employer/${employerId}/quota`),
    getPendingCount: (helperProfileId: number): Promise<ApiResponse<number>> =>
      api.get(`/intents/helper/${helperProfileId}/pending-count`),
  },
  // 數據字典（公開接口）
  dataDictionaries: {
    getByParentCode: (parentCode: string): Promise<ApiResponse<DataDictionary[]>> =>
      api.get(`/data-dictionaries/parent/${encodeURIComponent(parentCode)}`),
    getByParentCodes: (parentCodes: string[]): Promise<ApiResponse<DataDictionary[]>> =>
      api.post('/data-dictionaries/parent/batch', parentCodes),
  },
  // 公開 CMS 內容節點
  contentNodes: {
    getRoots: (): Promise<ApiResponse<ContentNodeResponse[]>> =>
      api.get('/content/nodes/roots'),
    getBySlug: (slug: string): Promise<ApiResponse<ContentNodeResponse>> =>
      api.get(`/content/nodes/slug/${encodeURIComponent(slug)}`),
  },
  // 聯絡資訊
  contactInfo: {
    get: (): Promise<ApiResponse<any>> => api.get('/contact-info'),
  },
  // SEO 及站點設定（社交媒體連結等）
  seoSettings: {
    get: (): Promise<ApiResponse<any>> => api.get('/seo-settings'),
  },
};


export default api;

